/**
 * Persistent storage for devise_token_auth headers.
 *
 * devise_token_auth sends four headers on successful login:
 * - access-token: short-lived JWT, rotated on each request
 * - client: identifies the client (browser tab, etc.)
 * - uid: user identifier (email)
 * - expiry: Unix timestamp when access-token expires
 *
 * We store all four in localStorage under a single JSON key to avoid
 * inconsistency. On 401 or logout, we clear everything atomically.
 */

export interface AuthHeaders {
  'access-token': string;
  client: string;
  uid: string;
  expiry: string;
}

const STORAGE_KEY = 'operator_console.auth';

/**
 * Save the full set of devise_token_auth headers atomically to localStorage.
 * Overwrites any previous headers.
 */
export function saveAuthHeaders(headers: AuthHeaders): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(headers));
}

/**
 * Load the stored devise_token_auth headers from localStorage.
 * Returns null if:
 *  - no headers are stored
 *  - localStorage contains malformed JSON
 *  - stored JSON is missing required fields
 */
export function loadAuthHeaders(): AuthHeaders | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<AuthHeaders>;
    // Validate all four required fields are present and are strings.
    if (
      typeof parsed['access-token'] === 'string' &&
      typeof parsed.client === 'string' &&
      typeof parsed.uid === 'string' &&
      typeof parsed.expiry === 'string'
    ) {
      return parsed as AuthHeaders;
    }
  } catch {
    // ignore malformed JSON
  }

  return null;
}

/**
 * Clear stored devise_token_auth headers from localStorage.
 * Idempotent: safe to call even if no headers are stored.
 */
export function clearAuthHeaders(): void {
  localStorage.removeItem(STORAGE_KEY);
}
